// src/components/notify/NotifyAiDigest.js
// FlowSense AI digest at the top of the notification centre: what needs attention first, and
// categories the user never reads (with a one-click "mute push").
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, IconButton, Skeleton, Text, Tooltip, useColorModeValue, VStack,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdExpandLess, MdExpandMore, MdRefresh, MdVolumeOff } from 'react-icons/md';
import * as api from '../../services/notifyApi';

function NotifyAiDigest({ refreshKey = 0, onMuteCategory, onFocus }) {
    const gradient = useColorModeValue('linear(to-r, purple.50, teal.50)', 'linear(to-r, purple.900, teal.900)');
    const border = useColorModeValue('purple.100', 'purple.700');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const [open, setOpen] = useState(true);
    const [muting, setMuting] = useState(null);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const load = useCallback(async (signal) => {
        setState(s => ({ ...s, loading: !s.data, error: null }));
        try {
            const data = await api.getDigest(signal);
            if (isMounted.current) setState({ loading: false, data, error: null });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setState(s => ({ ...s, loading: false, error: e }));
        }
    }, []);

    useEffect(() => {
        const ctrl = new AbortController();
        const t = setTimeout(() => load(ctrl.signal), refreshKey ? 600 : 0); // coalesce bursts
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [load, refreshKey]);

    const mute = useCallback(async (s) => {
        setMuting(s.category);
        try { await onMuteCategory?.(s.category); await load(); } finally { if (isMounted.current) setMuting(null); }
    }, [onMuteCategory, load]);

    const d = state.data;
    return (
        <Box bgGradient={gradient} borderWidth="1px" borderColor={border} borderRadius="xl" px={3} py={2.5} mx={3} mt={3}>
            <Flex align="center" justify="space-between" gap={2}>
                <HStack spacing={2} minW={0}>
                    <Icon as={MdAutoAwesome} color="purple.500" />
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wide" color="purple.600">FlowSense AI digest</Text>
                </HStack>
                <HStack spacing={0}>
                    <Tooltip label="Refresh digest" hasArrow><IconButton size="xs" variant="ghost" icon={<MdRefresh />} aria-label="Refresh digest" onClick={() => load()} /></Tooltip>
                    <IconButton size="xs" variant="ghost" icon={open ? <MdExpandLess /> : <MdExpandMore />} aria-label={open ? 'Collapse digest' : 'Expand digest'} onClick={() => setOpen(o => !o)} />
                </HStack>
            </Flex>
            {state.loading ? <Skeleton h="16px" mt={2} borderRadius="md" /> : state.error ? (
                <Text fontSize="xs" color={muted} mt={1}>Digest unavailable — {state.error.message}</Text>
            ) : d && (
                <>
                    <Text fontSize="sm" fontWeight="700" mt={1}>{d.headline}</Text>
                    {open && (
                        <VStack align="stretch" spacing={1.5} mt={1.5}>
                            {d.items.map((it) => (
                                <HStack key={it.kind} spacing={2} align="flex-start">
                                    <Box w="6px" h="6px" mt="7px" borderRadius="full" bg={it.level === 'critical' ? 'red.400' : 'purple.400'} flexShrink={0} />
                                    <Text fontSize="xs" color={muted}>{it.text}</Text>
                                </HStack>
                            ))}
                            {d.focus?.length > 0 && onFocus && (
                                <HStack spacing={1} flexWrap="wrap">
                                    {d.focus.slice(0, 3).map(f => (
                                        <Button key={f.id} size="xs" variant="outline" colorScheme={f.priority === 'critical' ? 'red' : 'purple'} onClick={() => onFocus(f)} maxW="100%">
                                            <Text as="span" noOfLines={1}>{f.title}</Text>
                                        </Button>
                                    ))}
                                </HStack>
                            )}
                            {d.suggestions?.map(s => (
                                <Flex key={s.category} align="center" justify="space-between" gap={2}>
                                    <Text fontSize="xs" color={muted}>{s.text}</Text>
                                    {onMuteCategory && (
                                        <Button size="xs" leftIcon={<MdVolumeOff />} variant="ghost" colorScheme="purple" flexShrink={0} isLoading={muting === s.category} onClick={() => mute(s)}>Mute</Button>
                                    )}
                                </Flex>
                            ))}
                            <HStack spacing={1}><Badge variant="subtle" colorScheme="purple" fontSize="2xs">{d.model}</Badge></HStack>
                        </VStack>
                    )}
                </>
            )}
        </Box>
    );
}

export default memo(NotifyAiDigest);
